import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageWrap, FadeIn } from '../components/Section';

const catalog = [
  { name: 'Soil Testing Machine', model: 'Direct Shear & Triaxial Analyzers', app: 'Measures soil shear strength parameters and cohesion', cat: 'Geotechnical' },
  { name: 'CBR Testing Machine', model: 'Motorized California Bearing Ratio System', app: 'Evaluates subgrade soil strength for pavement design', cat: 'Geotechnical' },
  { name: 'Universal Testing Machine (UTM)', model: 'Multi-ton Hydraulic/Electromechanical', app: 'Tests tensile, compressive, and transverse strength of steel', cat: 'Materials' },
  { name: 'Compression Testing Machine (CTM)', model: 'Digital Compressive Strength Tester', app: 'Measures compressive strength of concrete cubes and bricks', cat: 'Materials' },
  { name: 'Aggregate Testing Machine', model: 'Los Angeles Abrasion & Impact Testers', app: 'Determines aggregate hardness and durability', cat: 'Materials' },
];

export default function Equipment() {
  return (
    <PageWrap>
      <section style={{ padding: '160px 0 80px', background: 'var(--warm)' }}>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 16 }}>Products</p>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 20 }}>
              Testing Equipment &<br /><span style={{ color: 'var(--primary-light)' }}>Machinery Supply</span>
            </h1>
            <p style={{ fontSize: '1.08rem', color: 'var(--text-mid)', maxWidth: 650, lineHeight: 1.7, marginBottom: 32 }}>
              Unique among regional labs, Hicon operates a dedicated business unit for supply and distribution of civil engineering testing equipment — serving project sites, builders, and labs across Central India.
            </p>
            <Link to="/contact" className="btn btn-primary">Request Equipment Quote</Link>
          </motion.div>
        </div>
      </section>

      {/* USP */}
      <section style={{ padding: '48px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <div style={{ display: 'flex', alignItems: 'center', gap: 24, padding: '32px 36px', background: 'linear-gradient(135deg, rgba(45,74,45,0.04), rgba(201,169,97,0.06))', border: '1px solid var(--border)', borderRadius: 'var(--radius)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '2.5rem' }}>⚡</span>
              <div>
                <h3 style={{ fontSize: '1.15rem', marginBottom: 6 }}>Closed-Loop Quality Ecosystem</h3>
                <p style={{ fontSize: '0.93rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>We use our own calibrated machines in NABL-certified daily operations. Every machine we sell is proven in our laboratory first — a practical demonstration of accuracy and reliability.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Catalog */}
      <section style={{ padding: '100px 0', background: 'var(--cream)' }}>
        <div className="container">
          <FadeIn>
            <h2 style={{ fontSize: '2.2rem', marginBottom: 12 }}>Equipment Catalog</h2>
            <p style={{ color: 'var(--text-light)', marginBottom: 48 }}>Professional-grade testing machines, calibrated and ready for deployment.</p>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {catalog.map((item, i) => (
              <FadeIn key={item.name} delay={i * 0.08}>
                <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 32, display: 'flex', flexDirection: 'column', height: '100%', transition: 'all 0.3s' }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                  <span style={{ padding: '4px 10px', background: 'rgba(45,74,45,0.06)', borderRadius: 4, fontSize: '0.73rem', fontWeight: 600, color: 'var(--primary)', alignSelf: 'flex-start', marginBottom: 16 }}>{item.cat}</span>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: 8 }}>{item.name}</h3>
                  <p style={{ fontSize: '0.83rem', color: 'var(--text-light)', marginBottom: 12 }}>{item.model}</p>
                  <p style={{ fontSize: '0.88rem', color: 'var(--text-mid)', lineHeight: 1.5, flex: 1 }}>{item.app}</p>
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                    <Link to="/contact" style={{ fontSize: '0.86rem', fontWeight: 500, color: 'var(--primary)' }}>Request RFQ →</Link>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section style={{ padding: '100px 0', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn><h2 style={{ fontSize: '2.2rem', textAlign: 'center', marginBottom: 48 }}>Why Choose Hicon Equipment</h2></FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
            {[
              { t: 'Lab-Proven Accuracy', d: 'Every machine validated in our own NABL-accredited operations before sale.' },
              { t: 'Calibration Included', d: 'Equipment ships fully calibrated with documentation.' },
              { t: 'After-Sales Support', d: 'Ongoing maintenance, recalibration, and technical support.' },
              { t: 'Central India Network', d: 'Quick deployment across MP, UP, Maharashtra, and Rajasthan.' },
            ].map((item, i) => (
              <FadeIn key={item.t} delay={i * 0.1}>
                <div style={{ padding: 32, background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <h4 style={{ fontSize: '1.05rem', marginBottom: 8 }}>{item.t}</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-mid)', lineHeight: 1.6 }}>{item.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '0 0 100px', background: 'var(--white)' }}>
        <div className="container">
          <FadeIn>
            <div style={{ background: 'var(--primary-dark)', borderRadius: 20, padding: 'clamp(40px, 5vw, 64px)', textAlign: 'center' }}>
              <h2 style={{ fontSize: '2rem', color: 'white', marginBottom: 16 }}>Looking for Testing Equipment?</h2>
              <p style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.8)', maxWidth: 480, margin: '0 auto 32px' }}>From individual machines to complete lab setup — we supply calibrated, proven equipment.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
                <Link to="/contact" className="btn btn-accent">Download Catalog</Link>
                <Link to="/contact" className="btn btn-outline" style={{ borderColor: 'white', color: 'white' }}>Request Custom Quote</Link>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </PageWrap>
  );
}
